import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  InputAdornment,
  Radio,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  tableCellClasses,
  styled,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import { toast } from "react-toastify";
import { usersService } from "../../utils/services/usersService";
import { assignUserService } from "../../utils/services/assignUserService";
import { roleService } from "../../utils/services/role.service";
import { fieldSx } from "../../pages/manage-catalog/constants";

const StyledTableCell = styled(TableCell)(() => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: "var(--table-header-bg)",
    border: "1px solid var(--border-color)",
    color: "var(--text-primary)",
    padding: "6px 12px",
    fontFamily: "Poppins",
    fontSize: 13,
    fontWeight: 600,
  },
  [`&.${tableCellClasses.body}`]: {
    padding: "6px 12px",
    color: "var(--text-primary)",
    fontFamily: "Poppins",
    fontSize: 12,
    border: "1px solid var(--border-color)",
  },
}));

const StyledTableRow = styled(TableRow)(() => ({
  "&:nth-of-type(odd)": { backgroundColor: "var(--row-bg-odd)" },
  "&:nth-of-type(even)": { backgroundColor: "var(--row-bg-even)" },
  "&:hover": { backgroundColor: "var(--hover-bg)", cursor: "pointer" },
}));

const AssignExistingUserModal = ({ open, onClose, clientId, onSuccess }) => {
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [assignedUsers, setAssignedUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [searchValue, setSearchValue] = useState("");
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const CLIENT_ROLE_NAMES = ["Client - Standard", "Client - Premium"];

  const fetchData = async () => {
    setLoading(true);
    try {
      const [rolesRes, assignedRes] = await Promise.all([
        roleService.fetch(100, 0),
        assignUserService.fetch(200, 0, clientId),
      ]);

      const clientRoleIds = (rolesRes.data || [])
        .filter((r) => CLIENT_ROLE_NAMES.includes(r.name))
        .map((r) => r._id);

      const assigned = (assignedRes.data || []).map((m) => m.user);
      const assignedIds = new Set(assigned.map((u) => u?._id).filter(Boolean));

      let usersRes = { data: [] };
      if (clientRoleIds.length > 0) {
        const roleQuery = clientRoleIds
          .map((id, i) => `roles[$in][${i}]=${id}`)
          .join("&");
        usersRes = await usersService.fetchView(200, 0, null, roleQuery);
      }

      const availableUsers = (usersRes.data || []).filter(
        (u) => !assignedIds.has(u._id)
      );

      setAssignedUsers(assigned.filter(Boolean));
      setUsers(availableUsers);
      setFilteredUsers(availableUsers);
    } catch (error) {
      console.error("Fetch failed:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) {
      setSelectedUser(null);
      setSearchValue("");
      fetchData();
    }
  }, [open]);

  useEffect(() => {
    const lower = searchValue.toLowerCase();
    setFilteredUsers(
      users.filter(
        (u) =>
          u.name?.toLowerCase().includes(lower) ||
          u.email?.toLowerCase().includes(lower)
      )
    );
  }, [searchValue, users]);

  const handleConfirm = async () => {
    if (!selectedUser) {
      toast.error("Please select a user to assign.");
      return;
    }
    setSubmitting(true);
    try {
      await assignUserService.assignExistingUser(selectedUser._id, clientId);
      toast.success("User assigned successfully!");
      onSuccess && onSuccess();
      onClose();
    } catch (error) {
      toast.error("Failed to assign user.");
      console.error(error);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          backgroundColor: "var(--bg-paper)",
          backgroundImage: "none",
          borderRadius: 2,
        },
      }}
    >
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <DialogTitle
          sx={{
            fontFamily: "Poppins",
            fontSize: 20,
            fontWeight: 600,
            color: "var(--text-primary)",
            pb: 1,
          }}
        >
          Assign User
        </DialogTitle>
        <IconButton onClick={onClose} sx={{ mr: 1 }}>
          <CloseIcon sx={{ color: "var(--text-secondary)" }} />
        </IconButton>
      </Box>
      <Divider />

      <DialogContent sx={{ display: "flex", gap: 2, minHeight: 360 }}>
        {/* Left: selectable users */}
        <Box sx={{ flex: 1, display: "flex", flexDirection: "column" }}>
          <Typography
            fontFamily="Poppins"
            fontSize={13}
            fontWeight={600}
            color="var(--text-primary)"
            sx={{ mb: 1 }}
          >
            Available Users
          </Typography>
          <TextField
            size="small"
            fullWidth
            placeholder="Search by name or email"
            value={searchValue}
            onChange={(e) => setSearchValue(e.target.value)}
            sx={{ mb: 1.5, ...fieldSx }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: "var(--text-secondary)", fontSize: 20 }} />
                </InputAdornment>
              ),
            }}
          />

          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <CircularProgress size={28} />
            </Box>
          ) : filteredUsers.length === 0 ? (
            <Typography
              fontFamily="Poppins"
              fontSize={13}
              color="var(--text-secondary)"
              textAlign="center"
              sx={{ py: 3 }}
            >
              No users with Client - Standard or Client - Premium role found.
            </Typography>
          ) : (
            <TableContainer
              sx={{
                flex: 1,
                maxHeight: 340,
                border: "1px solid var(--border-color)",
                borderRadius: 1,
              }}
            >
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow>
                    <StyledTableCell sx={{ width: 36 }} />
                    <StyledTableCell>Name</StyledTableCell>
                    <StyledTableCell>Email</StyledTableCell>
                    <StyledTableCell>Phone</StyledTableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredUsers.map((user) => (
                    <StyledTableRow
                      key={user._id}
                      onClick={() => setSelectedUser(user)}
                    >
                      <StyledTableCell>
                        <Radio
                          size="small"
                          checked={selectedUser?._id === user._id}
                          onChange={() => setSelectedUser(user)}
                          onClick={(e) => e.stopPropagation()}
                          sx={{ p: 0.5 }}
                        />
                      </StyledTableCell>
                      <StyledTableCell>
                        <Typography fontFamily="Poppins" fontSize={12} color="var(--text-primary)">
                          {user.name}
                        </Typography>
                      </StyledTableCell>
                      <StyledTableCell>
                        <Typography fontFamily="Poppins" fontSize={12} color="var(--text-secondary)">
                          {user.email}
                        </Typography>
                      </StyledTableCell>
                      <StyledTableCell>
                        <Typography fontFamily="Poppins" fontSize={12} color="var(--text-secondary)">
                          {user.phone_no || "—"}
                        </Typography>
                      </StyledTableCell>
                    </StyledTableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Box>

        {/* Divider */}
        <Divider orientation="vertical" flexItem />

        {/* Right: already assigned users */}
        <Box sx={{ width: 220, display: "flex", flexDirection: "column" }}>
          <Typography
            fontFamily="Poppins"
            fontSize={13}
            fontWeight={600}
            color="var(--text-primary)"
            sx={{ mb: 1 }}
          >
            Already Assigned
          </Typography>

          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", pt: 2 }}>
              <CircularProgress size={22} />
            </Box>
          ) : assignedUsers.length === 0 ? (
            <Typography
              fontFamily="Poppins"
              fontSize={12}
              color="var(--text-secondary)"
              sx={{ pt: 1 }}
            >
              No users assigned yet.
            </Typography>
          ) : (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 1,
                overflowY: "auto",
                maxHeight: 340,
                pr: 0.5,
              }}
            >
              {assignedUsers.map((u) => (
                <Box
                  key={u._id}
                  sx={{
                    border: "1px solid var(--border-color)",
                    borderRadius: 1,
                    px: 1.5,
                    py: 0.8,
                    backgroundColor: "var(--bg-primary)",
                  }}
                >
                  <Typography
                    fontFamily="Poppins"
                    fontSize={12}
                    fontWeight={600}
                    color="var(--text-primary)"
                    noWrap
                  >
                    {u.name}
                  </Typography>
                  <Typography
                    fontFamily="Poppins"
                    fontSize={11}
                    color="var(--text-secondary)"
                    noWrap
                  >
                    {u.email}
                  </Typography>
                </Box>
              ))}
            </Box>
          )}
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
        <Button
          onClick={onClose}
          variant="outlined"
          sx={{
            fontFamily: "Poppins",
            fontSize: 13,
            fontWeight: 600,
            textTransform: "none",
            borderColor: "var(--border-color)",
            color: "var(--text-secondary)",
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleConfirm}
          variant="contained"
          disabled={!selectedUser || submitting}
          sx={{
            fontFamily: "Poppins",
            fontSize: 13,
            fontWeight: 600,
            textTransform: "none",
            minWidth: 100,
          }}
        >
          {submitting ? <CircularProgress size={18} color="inherit" /> : "Assign"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AssignExistingUserModal;
